/** @format */

import { useState, useEffect, useCallback, useRef } from "react";
import { getMadrasahManual } from "../../services/v2/planningservice/uploadmanualservices";
import {
  getPlanningAll,
  tolakPengajuanVerifikasiDokumenBos,
} from "../../services/v2/planningservice";
import { postDokumenPencairan } from "services/v2/planningservice/pencairanservices";

interface Props {
  auth: any;
  activeTabKey: string | null;
}

const CACHE_TTL = 30 * 60 * 1000;

export const useVerificator = ({ auth, activeTabKey }: Props) => {
  const [madrasah, setMadrasah] = useState<any[]>([]);
  const [listMadrasahAlokasi, setListMadrasahAlokasi] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const requestIdRef = useRef(0);
  const abortControllerRef = useRef<AbortController | null>(null);

  const cacheRef = useRef<Record<string, any>>({});

  const fetchData = useCallback(
    async (force = false) => {
      if (!auth?.isTahun || !activeTabKey) return;

      const cacheKey = `${auth.isTahun}-${auth.kode_provinsi}-${auth.kode_kabkota}-${activeTabKey}`;

      const cached = cacheRef.current[cacheKey];

      if (!force && cached && Date.now() - cached.time < CACHE_TTL) {
        setMadrasah(cached.madrasah);
        setListMadrasahAlokasi(cached.alokasi);
        return;
      }

      requestIdRef.current += 1;
      const requestId = requestIdRef.current;

      abortControllerRef.current?.abort();
      abortControllerRef.current = new AbortController();

      setLoading(true);

      try {
        let filterParam = `?kodeProvinsi=${auth.kode_provinsi}&tahun=${auth.isTahun}`;

        if (auth.group_role !== "provinsi") {
          filterParam += `&kodeKabkota=${auth.kode_kabkota}`;
        }

        filterParam += `&jenisMadrasah=R`;

        const [resAlokasiList, madrasahTmp] = await Promise.all([
          getPlanningAll("bos/list-alokasi-by-sumberdana", {
            tahun: auth.isTahun,
            kodeSumberDana: activeTabKey,
          }),
          getMadrasahManual(filterParam),
        ]);

        if (requestId !== requestIdRef.current) return;

        const dataMadrasah = madrasahTmp || [];
        const dataAlokasi = resAlokasiList || [];

        setMadrasah(dataMadrasah);
        setListMadrasahAlokasi(dataAlokasi);

        cacheRef.current[cacheKey] = {
          madrasah: dataMadrasah,
          alokasi: dataAlokasi,
          time: Date.now(),
        };
      } catch (error: any) {
        if (error?.name !== "AbortError") {
          console.error(error);
        }
      } finally {
        if (requestId === requestIdRef.current) {
          setLoading(false);
        }
      }
    },
    [
      auth?.isTahun,
      auth?.kode_provinsi,
      auth?.kode_kabkota,
      auth?.group_role,
      activeTabKey,
    ],
  );

  /**
   * FETCH ketika parameter berubah
   */
  useEffect(() => {
    if (!auth?.isTahun || !activeTabKey) return;

    setMadrasah([]);
    setListMadrasahAlokasi([]);

    fetchData(true);

    return () => {
      abortControllerRef.current?.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth?.isTahun, auth?.kode_provinsi, auth?.kode_kabkota, activeTabKey]);

  const confirmDocument = useCallback(
    async (payload: any, type: string) => {
      setLoading(true);

      try {
        let res;

        if (type === "approve") {
          res = await postDokumenPencairan(payload);
        } else {
          res = await tolakPengajuanVerifikasiDokumenBos(payload);
        }

        cacheRef.current = {};

        await fetchData(true);

        return res;
      } catch (error: any) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    },
    [fetchData],
  );

  return {
    madrasah,
    listMadrasahAlokasi,
    loading,
    reload: fetchData,
    confirmDocument,
  };
};
